import { useMemo } from "react";
import type { LineChartItem } from "@/lib/utils/marketSimilarity/types";

const MUTED_LINE_COLOR = "#9ca3af";

// Memoized color name computation with caching
const colorNameCache = new Map<string, string>();

function getColorName(color: string): string {
  if (colorNameCache.has(color)) {
    return colorNameCache.get(color)!;
  }

  const colorLower = color.toLowerCase().trim();
  let result: string;

  // Check for named colors first
  if (colorLower.includes("blue")) result = "Avg Correl";
  else if (colorLower.includes("gray") || colorLower.includes("grey")) result = "Neither";
  else if (colorLower.includes("red")) result = "Euclidean";
  else if (colorLower.includes("green")) result = "Green";
  else if (colorLower.includes("purple") || colorLower.includes("violet")) result = "Both";
  else if (colorLower.includes("orange")) result = "Orange";
  else if (colorLower.includes("yellow")) result = "Yellow";
  else if (colorLower.includes("pink")) result = "Pink";
  else if (colorLower.includes("cyan")) result = "Cyan";
  else if (colorLower.includes("magenta")) result = "Magenta";
  else { result = "Unknown"; }

  colorNameCache.set(color, result);
  return result;
}

function parseDateString(dateString: string): Date {
  const [month, day, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
}

export type TimelinePoint = {
  dateString: string;
  value: number;
  colorName: string;
  colorValue: string;
};

export type ProcessedLineChartData = {
  timelinePoints: TimelinePoint[];
  xAxisData: string[];
  legendNames: string[];
  colorNameToColorValue: Record<string, string>;
  dateStringToPosition: Map<string, number>;
  neitherColor: string;
};

/**
 * Hook to process raw line chart data into the format needed by the chart.
 * Exposes xAxisData and other processed structures for use in zoom calculations.
 */
export function useProcessedLineChartData(
  data: LineChartItem[]
): ProcessedLineChartData | null {
  return useMemo(() => {
    if (data.length === 0) {
      return null;
    }

    const dateEntryMap = new Map<
      string,
      {
        date: Date;
        value: number;
        colorName: string;
        colorValue: string;
        dateIndex: number;
      }
    >();
    const colorNameToColorValue: Record<string, string> = {};

    for (const item of data) {
      const colorName = getColorName(item.color);
      const colorValue = item.color;

      if (!(colorName in colorNameToColorValue)) {
        let mappedColor = colorValue;
        if (colorName === "Neither") {
          mappedColor = "#dcd2d2";
        }
        colorNameToColorValue[colorName] = mappedColor;
      }

      for (const point of item.points) {
        const parsedDate = parseDateString(point.dateString);
        const existingEntry = dateEntryMap.get(point.dateString);

        if (!existingEntry || point.dateIndex >= existingEntry.dateIndex) {
          dateEntryMap.set(point.dateString, {
            date: parsedDate,
            value: point.adjustedClose,
            colorName,
            colorValue,
            dateIndex: point.dateIndex,
          });
        }
      }
    }

    const timelinePoints: TimelinePoint[] = Array.from(dateEntryMap.entries())
      .sort((a, b) => a[1].date.getTime() - b[1].date.getTime())
      .map(([dateString, info]) => ({
        dateString,
        value: info.value,
        colorName: info.colorName,
        colorValue: info.colorValue,
      }));

    const xAxisData = timelinePoints.map((point) => point.dateString);
    const dateStringToPosition = new Map<string, number>();
    timelinePoints.forEach((point, index) => {
      dateStringToPosition.set(point.dateString, index);
    });
    const legendNames = Object.keys(colorNameToColorValue);
    const neitherColor = colorNameToColorValue["Neither"] ?? MUTED_LINE_COLOR;

    return {
      timelinePoints,
      xAxisData,
      legendNames,
      colorNameToColorValue,
      dateStringToPosition,
      neitherColor,
    };
  }, [data]);
}
